/*
  사원증 결제 관리 (회사 관리 > 공휴일 관리 다음)

  - LNB 노출/진입 허용 고객사는 constants/employeeCardPayment 의 화이트리스트로 통제한다.
  - 필터(식대그룹/검색어)는 기존 공통 컴포넌트를 재사용하며 값은 redux(state.commons)에 담긴다.
    검색어는 입력마다 dispatch 되므로 이 컨테이너는 state.commons.text 를 구독하지 않고
    handleChange 콜백 → ref 로 받아 조회 시점에만 사용한다(입력 중 리렌더링 방지).
  - 부서는 '사용자별 복지포인트 사용통계'와 같은 방식으로 getDivision 트리를 '상위>하위' 경로로 평탄화해
    쓴다. 필터는 depth 별 셀렉트(components/Commons/Division) 대신 검색 가능한 단일 셀렉트,
    표도 depth 별 컬럼(cm.tableHeaderList) 대신 전체 경로 한 칸으로 보여준다.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Button, Dropdown, Form, Input } from 'semantic-ui-react';

import { getDivision } from 'apis/captainpayment';
import { getEmployeeCardPaymentList } from 'apis/employeeCardPayment';
import { LoadingBar, Pagination, SikdaeGroup, TableForm } from 'components/Commons';
import {
  EMPLOYEE_CARD_PAYMENT_DEFAULT_PAGE_SIZE,
  EMPLOYEE_CARD_PAYMENT_PAGE_SIZE_OPTIONS,
  hasEmployeeCardPaymentAuth,
  isEmployeeCardPaymentAllowedCompany
} from 'constants/employeeCardPayment';
import * as cmAction from 'actions/commons';
import cm from 'helpers/commons';
import storage from 'helpers/storage';
import { safeHistoryPush } from 'helpers/pathValidator';

import EmployeeCardPaymentModal from './EmployeeCardPaymentModal';

const EXCEL_PAGE_URL = '/main/employeeCardPaymentByExcel';

const EMPTY_BEFORE_SEARCH = '[조회] 후 확인 가능합니다.';
const EMPTY_AFTER_SEARCH = '조건에 맞는 사용자가 없습니다. 조건을 바꿔 다시 조회해 주세요.';

const STATUS_OPTIONS = [
  { value: 'ALL', text: '사용자 상태 전체' },
  { value: 'ACTIVE', text: '활성' },
  { value: 'INACTIVE', text: '일시정지' }
];

const REGISTERED_OPTIONS = [
  { value: 'ALL', text: '등록 여부 전체' },
  { value: 'REGISTERED', text: '등록' },
  { value: 'UNREGISTERED', text: '미등록' }
];

const DIVISION_ALL = 'ALL';

/*
  컬럼 폭. 합이 1720px 이라 대부분의 창에서 가로 스크롤이 생긴다.
  스크롤 때문에 오른쪽 끝 [관리] 버튼이 안 보일 수 있어, 행 아무 데나 눌러도 상세가 열린다.
  사원증 결제정보는 최대 30자(영문·숫자)가 잘리지 않을 폭이다.
*/
const defaultColumns = [
  { header: 'ID', name: 'signId', width: 150 },
  { header: '이름', name: 'name', width: 150 },
  { header: '식대그룹', name: 'groupName', width: 150 },
  { header: '부서', name: 'orgPathName', width: 200 },
  { header: '이메일', name: 'email', width: 150 },
  { header: '휴대전화번호', name: 'phone', width: 120 },
  { header: '사원번호', name: 'employeeId', width: 100 },
  { header: '사용자 상태', name: 'statusText', width: 100 },
  { header: '가입일', name: 'joinDate', width: 100 },
  { header: '사원증 결제정보', name: 'barCode', width: 300 },
  { header: '', name: 'manage', width: 100 }
];

/*
  [생략 표기] 값이 폭보다 길면 컬럼이 밀려나 표 전체가 넓어지고 가로 스크롤이 생긴다.
  [관리] 버튼을 뺀 모든 열에서 넘치는 만큼 ...으로 줄이고, 전체 값은 마우스를 올려 title 로 보여준다.
*/

/*
  표는 auto layout 이라 셀에 width 만 주면 '권장 폭'일 뿐, 창이 좁아지면 그 아래로 눌린다.
  컬럼마다 min-width 를 함께 걸어 눌리지 않게 하고, 모자라는 만큼은 가로 스크롤로 넘긴다.
  TableForm 이 머리글에는 width 를 넘기지 않아(columnWidth 미사용) 여기서 th 까지 함께 지정한다.
*/
const TABLE_MIN_WIDTH = defaultColumns.reduce((total, column) => total + column.width, 0);

const COLUMN_WIDTH_RULES = defaultColumns
  .map(
    (column, index) => `
    th:nth-child(${index + 1}),
    td:nth-child(${index + 1}) {
      width: ${column.width}px;
      min-width: ${column.width}px;
    }`
  )
  .join('');

// semantic 테이블 셀의 좌우 패딩(0.78571429em × 2 @ 14px)
const CELL_HORIZONTAL_PADDING = 22;

/*
  상단 필터 한 줄의 폭. 표(1720px)보다 좁아서 필터는 스크롤 없이 다 보이고 표만 넘친다.
*/
// semantic .ui.selection.dropdown 은 min-width 14em(196px)이 기본이라, 아래 CSS 에서 함께 풀어야 이 폭이 먹는다
const SELECT_WIDTH = 175;
const DIVISION_SELECT_WIDTH = 185;
// placeholder('ID, 이름, 이메일, 휴대전화번호, 사원번호, 사원증 결제정보') 약 364px + input 좌우 패딩 + 여백
const KEYWORD_FIELD_WIDTH = 430;
const SUBMIT_BUTTON_WIDTH = 145;

const Wrapper = styled.main`
  .excel-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 240px;
    height: 33px;
    padding: 0px;
    line-height: 26px;
  }
  /*
    필터는 좁은 창에서도 한 줄을 유지한다(조회 버튼이 아래로 내려가지 않게).
    넘치는 만큼은 콘텐츠 영역의 가로 스크롤로 넘긴다.
  */
  .search-form .fields {
    align-items: center;
    flex-wrap: nowrap;
    /*
      semantic 은 .fields 에 좌우 -0.5em 마진을, .field 에 좌우 0.5em 패딩을 줘서 서로 상쇄시킨다.
      이 상쇄에 기대면 제목·표와 좌우 끝이 어긋나므로, 마진과 양끝 패딩을 함께 0 으로 만든다.
    */
    margin-left: 0px;
    margin-right: 0px;
  }
  .search-form .fields > .field:first-child {
    padding-left: 0px;
  }
  /*
    .field 는 블록인데 semantic 이 그 안의 컨트롤을 inline 계열로 만든다
    (.selection.dropdown → inline-block / .ui.input → inline-flex + vertical-align: middle).
    그래서 라인박스 베이스라인 때문에 셀렉트와 검색 input 의 세로 위치가 어긋난다.
    .field 를 flex 로 바꿔 베이스라인 영향을 없애고 컨트롤을 가운데 맞춘다.
  */
  .search-form .fields > .field {
    display: flex;
    align-items: center;
    /* 줄바꿈 대신 스크롤이므로 컨트롤이 찌그러지지 않게 축소를 막는다 */
    flex: 0 0 auto;
  }
  /*
    placeholder('ID, 이름, 이메일, 휴대전화번호, 사원번호, 사원증 결제정보')가 잘리지 않을 고정 폭.
    안쪽 input 에만 width 를 주면 semantic 의 .ui.form input 규칙(width:100%)과 flex 축소에 눌려
    반영되지 않는다. 바깥 .field 폭을 고정하고 안쪽이 그 폭을 채우게 한다.
    잘리면 아래 500px 만 키우면 된다.
  */
  .search-form .fields > .field.keyword-field {
    flex: 0 0 auto;
    width: ${KEYWORD_FIELD_WIDTH}px;
  }
  /*
    semantic 의 .ui.form .inline.field:not(.wide) .ui.input 규칙이 width 를 auto 로 두는데,
    :not(.wide) 까지 세면 클래스 7개라 여기서 이길 수 없다. !important 로 덮는다.
    이게 auto 로 남으면 .ui.input 이 내용 크기로 줄어, 안쪽 input 의 width:100% 가
    기준 폭을 못 찾아 그대로 좁게 남는다(= 바깥 div 만 넓어짐).
  */
  .search-form .keyword-field .ui.input {
    width: 100% !important;
  }
  .search-form .keyword-field .ui.input > input {
    width: 100%;
    min-width: 0;
  }
  /*
    조회 버튼을 우측 끝으로 민다.
    semantic 의 .ui.form .fields .field 규칙이 margin 0 을 클래스 4개로 걸기 때문에
    같은 수 이상으로 맞춰야 margin-left 가 먹는다.
    오른쪽 패딩은 .fields 의 마진을 0 으로 만든 만큼 함께 지운다.
  */
  .search-form .fields > .field.submit-field {
    margin-left: auto;
    padding-right: 0px;
  }
  /*
    semantic 의 .ui.button 은 기본 오른쪽 마진 0.25em 을 갖는다(셀렉트에는 없다).
    그대로 두면 조회 버튼만 그만큼 안쪽으로 들어가 아랫줄 셀렉트·표와 끝이 어긋난다.
  */
  .search-form .submit-field .ui.button {
    /*
      '사용자별 식대 내역'의 조회 버튼은 Grid 2/16 컬럼을 꽉 채워 1920 기준 약 200px 이다.
      이 화면은 flex 라 Grid 컬럼이 없으므로 같은 폭을 직접 준다. 높이는 semantic 기본값.
    */
    width: ${SUBMIT_BUTTON_WIDTH}px;
    /* semantic 의 .ui.button 기본 오른쪽 마진 0.25em 을 지워 표·셀렉트와 끝을 맞춘다 */
    margin-right: 0px;
  }
  /*
    셀렉트 폭. semantic 기본 min-width(14em = 196px)를 함께 풀지 않으면 이 값이 반영되지 않는다.
    부서는 이름이 길어 조금 더 준다.
  */
  .search-form .ui.selection.dropdown {
    min-width: ${SELECT_WIDTH}px;
    width: ${SELECT_WIDTH}px;
  }
  .search-form .ui.selection.dropdown.division-select {
    min-width: ${DIVISION_SELECT_WIDTH}px;
    width: ${DIVISION_SELECT_WIDTH}px;
  }

  /*
    [부서명 생략 표기] 나중에 뺄 수 있도록 여기 한 곳에 모아둔다.
    제거할 때는 이 블록과 tableData 의 division-cell span 래핑만 지우면 된다.

    글자 수로 문자열을 자르지 않고 폭 기준(text-overflow)으로 자른다.
    셀렉트는 search 가 '표시 문자열'을 매칭하므로 미리 자르면 부서명 검색이 깨진다.
  */
  .division-select > .text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .division-select .menu {
    max-width: 100%;
  }
  .division-select .menu > .item {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* 행 아무 데나 눌러도 상세가 열린다 */
  .tb-common tbody tr {
    cursor: pointer;
  }

  /* 폭은 컬럼마다 달라 셀에서 직접 준다 */
  .ellipsis-cell {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .page-hint {
    margin: 0px 0px 12px;
    color: #8e8e8e;
    /* 아래 '검색결과 : N건' 과 같은 크기 (semantic 기본 @fontSize) */
    font-size: 14px;
    line-height: 1.6;
  }
`;

const TableWrapper = styled.div`
  .ui.table {
    min-width: ${TABLE_MIN_WIDTH}px;
    margin-bottom: 16px;
    ${COLUMN_WIDTH_RULES}
  }
`;

const PageRowBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  /* 페이지 번호를 페이지 크기 셀렉트 왼쪽에 둔다 */
  .page-controls {
    display: flex;
    align-items: center;
  }
  .page-controls .ui.menu {
    margin: 0px 12px 0px 0px;
  }

  /*
    윗줄 조회 버튼과 같은 방식으로 오른쪽 끝을 맞춘다.
    .fields 의 오른쪽 음수 마진과 마지막 .field 의 오른쪽 패딩을 함께 0 으로 만든다.
    (아래 여백 @rowDistance 도 이 줄에서는 필요 없어 함께 지운다)
  */
  .ui.form .fields {
    margin-right: 0px;
    margin-bottom: 0px;
  }
  .ui.form .fields > .field:last-child {
    padding-right: 0px;
  }
`;

const EmployeeCardPayment = ({ history, groupIdx, commonsAPI }) => {
  const keywordRef = useRef(null);

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('ALL');
  const [registered, setRegistered] = useState('ALL');
  const [orgCode, setOrgCode] = useState(DIVISION_ALL);
  const [pageSize, setPageSize] = useState(EMPLOYEE_CARD_PAYMENT_DEFAULT_PAGE_SIZE);
  // 조회 버튼으로 확정된 검색 조건. null 이면 아직 한 번도 조회하지 않은 상태다.
  const [searchParams, setSearchParams] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAllowed = useMemo(() => {
    const company = storage.get('company') || {};
    const info = storage.get('info') || {};

    // LNB 에 안 보이는 사용자가 URL 로 들어오는 것까지 막는다(메뉴와 같은 권한 코드).
    return isEmployeeCardPaymentAllowedCompany(company.id) && hasEmployeeCardPaymentAuth(info.auth && info.auth.auth);
  }, []);

  useEffect(() => {
    if (!isAllowed) {
      safeHistoryPush(history, '/main/dashBoard');
      return;
    }

    cm.mainTitle('사원증 결제 관리');
    commonsAPI.sikdaeGroupList({ viewType: 'all' });
  }, []);

  const handlePageSizeChange = (e, item) => {
    setPageSize(item.value);
    setPage(1);
  };

  // 표의 부서 컬럼은 목록 응답의 orgPathName 을 쓰므로 여기서는 필터 옵션만 만든다.
  // 쿼리 키는 같은 API 를 쓰는 다른 화면과 캐시를 공유하도록 'getDivision' 으로 맞춘다.
  const { data: divisionData } = useQuery('getDivision', () => getDivision({}), { enabled: isAllowed });

  const divisionOptions = useMemo(() => {
    const navi = cm.divisionNavigation(divisionData && divisionData.division, null) || [];

    return [
      { key: DIVISION_ALL, value: DIVISION_ALL, text: '부서 전체' },
      // title 은 semantic 이 처리하지 않는 prop 이라 메뉴 항목 엘리먼트로 그대로 전달된다.
      // 이름이 ...으로 잘려도 마우스를 올리면 전체 경로가 보인다.
      ...navi
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({ key: item.orgCode, value: item.orgCode, text: item.name, title: item.name }))
    ];
  }, [divisionData]);

  // 닫힌 셀렉트의 선택값도 잘릴 수 있어 감싸는 field 에 툴팁을 준다.
  const selectedDivisionName = useMemo(() => {
    if (orgCode === DIVISION_ALL) return undefined;

    const selected = divisionOptions.filter((option) => option.value === orgCode)[0];
    return selected ? selected.text : undefined;
  }, [divisionOptions, orgCode]);

  const { data, isFetching } = useQuery(
    ['employeeCardPaymentList', searchParams, page, pageSize],
    // page 는 1부터 시작(API 기본값), size 는 페이지당 갯수
    () => getEmployeeCardPaymentList({ ...searchParams, page, size: pageSize }),
    {
      enabled: isAllowed && !!searchParams,
      keepPreviousData: true
      // 조회 실패는 COMPANY_API 의 공통 인터셉터가 안내 모달로 알린다(다른 목록 화면과 동일).
      // 여기서 토스트를 또 띄우면 같은 메시지가 두 번 나온다.
    }
  );

  const handleSubmit = () => {
    setPage(1);
    setSearchParams({
      status: status === 'ALL' ? undefined : status,
      hasBarCode: registered === 'ALL' ? undefined : registered === 'REGISTERED',
      groupIdx: groupIdx || undefined,
      orgCode: orgCode === DIVISION_ALL ? undefined : orgCode,
      keyword: keywordRef.current || undefined
    });
  };

  const handleEntSubmit = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const handleManageClick = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => setIsModalOpen(false);

  const moveExcelPage = () => safeHistoryPush(history, EXCEL_PAGE_URL);

  const tableData = useMemo(() => {
    const rows = data ? data.rows : [];
    const columns = defaultColumns.map((item) => ({ ...item, textAlign: 'left' }));
    const thead = columns.map((item) => ({ data: item.header, ...item }));

    const tbody = rows.map((item) => {
      const row = columns.map((column, columnIdx) => {
        // 관리 버튼은 TableForm 이 제공하는 btnFunc/btnData 로 그린다(셀마다 엘리먼트를 새로 만들지 않는다).
        if (column.name === 'manage') {
          return {
            ...column,
            data: '관리',
            btnFunc: handleManageClick,
            btnData: item,
            btnProps: { style: { width: '80px', height: '32px' } }
          };
        }

        let value = item[column.name];

        if (column.name === 'phone') {
          value = item.phone ? cm.phoneFormat(item.phone) : null;
        } else if (column.name === 'joinDate') {
          value = cm.yyyyMMdd(item.joinDate);
        }

        const text = value || '-';

        return {
          ...column,
          key: `${column.name}-${columnIdx}`,
          data: (
            <span className="ellipsis-cell" style={{ maxWidth: column.width - CELL_HORIZONTAL_PADDING }} title={text}>
              {text}
            </span>
          )
        };
      });

      // 가로 스크롤로 [관리] 버튼이 가려져도 열 수 있도록 행 전체를 누를 수 있게 한다.
      return { row, clickFunc: handleManageClick, clickData: item };
    });

    return {
      header: { main: thead },
      body: {
        main: tbody.length ? tbody : undefined,
        message: searchParams ? EMPTY_AFTER_SEARCH : EMPTY_BEFORE_SEARCH
      }
    };
  }, [data, searchParams]);

  const totalCount = data ? data.totalCount : 0;

  if (!isAllowed) return null;

  return (
    <Wrapper className="usgl">
      {isFetching ? (
        <LoadingBar style={{ position: 'fixed', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 10000 }} />
      ) : null}

      {/* 페이지 이름은 Main 레이아웃(.main-title)이 좌측에 렌더하고, .top-btn 이 그 우측에 붙는다 */}
      <div className="top-btn button">
        <Button inverted color="green" className="excel-button" onClick={moveExcelPage}>
          사원증 결제정보 엑셀파일로 관리
        </Button>
      </div>

      <p className="page-hint">
        사원증 결제정보만 관리할 수 있습니다. 그 외 정보는 사용자 관리 메뉴에서 관리할 수 있습니다.
      </p>

      <Form className="search-form" onSubmit={handleSubmit}>
        <Form.Group className="form-input">
          <Form.Field inline>
            <Dropdown selection options={STATUS_OPTIONS} value={status} onChange={(e, item) => setStatus(item.value)} />
          </Form.Field>
          <SikdaeGroup />
          <Form.Field inline title={selectedDivisionName}>
            <Dropdown
              selection
              search
              className="division-select"
              options={divisionOptions}
              value={orgCode}
              onChange={(e, item) => setOrgCode(item.value)}
            />
          </Form.Field>
          <Form.Field inline>
            <Dropdown
              selection
              options={REGISTERED_OPTIONS}
              value={registered}
              onChange={(e, item) => setRegistered(item.value)}
            />
          </Form.Field>
          <Form.Field inline className="keyword-field">
            <Input
              placeholder="ID, 이름, 이메일, 휴대전화번호, 사원번호, 사원증 결제정보"
              onChange={(e) => {
                keywordRef.current = e.target.value;
              }}
              onKeyPress={handleEntSubmit}
            />
          </Form.Field>
          <Form.Field inline className="submit-field">
            <Button color="black" content="조회" inverted />
          </Form.Field>
        </Form.Group>
      </Form>

      <PageRowBox>
        <span>검색결과 : {cm.numberComma(totalCount)}건</span>
        <div className="page-controls">
          {searchParams ? (
            <Pagination page={page} pageRow={pageSize} pageBlock={10} totalCount={totalCount} pageClick={setPage} />
          ) : null}
          <Dropdown
            selection
            compact
            options={EMPLOYEE_CARD_PAYMENT_PAGE_SIZE_OPTIONS}
            value={pageSize}
            onChange={handlePageSizeChange}
          />
        </div>
      </PageRowBox>

      {/* 컬럼 수가 많아 표는 넘칠 수 있다. .table-box 안에서만 가로 스크롤되도록 가둔다 */}
      <TableWrapper className="table-box">
        <TableForm selectable="true" tabledata={tableData} />
      </TableWrapper>

      <EmployeeCardPaymentModal open={isModalOpen} row={selectedRow} onClose={handleModalClose} />
    </Wrapper>
  );
};

export default connect(
  (state) => ({
    groupIdx: (state.commons.group || {}).value // 식대그룹 선택값
    // 식대그룹 목록(state.commons.sikdaeGroup)은 SikdaeGroup 컴포넌트가 직접 구독한다.
    // 표는 응답의 groupName 을 그대로 쓰므로 여기서 구독하지 않는다(불필요한 리렌더 방지).
  }),
  (dispatch) => ({
    commonsAPI: bindActionCreators({ sikdaeGroupList: cmAction.SikdaeGroupList }, dispatch)
  })
)(EmployeeCardPayment);
